import FaiTypography from "MuiComponents/FaiTypography";
import theme from "assets/theme";
import {
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	TextField,
} from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel } from "@mui/x-data-grid";
import { message } from "antd";
import Axios from "services/api/config";
import {
	getPaymentsList,
	processPayoutsURL,
	markPayoutEligibleURL,
	getPaymentConfigsURL,
	updatePaymentConfigURL,
} from "services/api/routes/Payments";

type ChipColor = "default" | "success" | "error" | "warning" | "info";

const statusColor = (status?: string): ChipColor => {
	const s = (status || "").toUpperCase();
	if (s === "PROCESSED") return "success";
	if (s === "WITHHELD") return "error";
	if (s === "ELIGIBLE_FOR_PAYOUT") return "info";
	if (s === "PENDING") return "warning";
	return "default";
};

// Flatten a backend MerchantPayoutResponseDTO into a flat table row.
const toRow = (p: any) => ({
	id: p?.id,
	merchantId: p?.merchantId || "-",
	order: p?.razorpayOrderId || p?.subOrderId || "-",
	gross: `₹ ${p?.grossAmount ?? 0}`,
	commission: `₹ ${p?.commissionAmount ?? 0}`,
	net: `₹ ${p?.netAmount ?? 0}`,
	eligibleBool: !!p?.isEligibleForPayout,
	status: p?.status || "-",
	date: p?.createdAt ? new Date(p.createdAt).toLocaleDateString() : "-",
	raw: p,
});

function Payments() {
	const { typography } = theme;
	const [payments, setPayments] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [running, setRunning] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	// Payout cron config (type MERCHANT_PAYOUT_CRON_JOB).
	const [cronConfig, setCronConfig] = useState<{ id?: string; cron: string }>({
		cron: "",
	});
	const [savingCron, setSavingCron] = useState(false);

	const fetchPayments = useCallback(async () => {
		try {
			setLoading(true);
			const query = `page=${paginationModel.page}&size=${paginationModel.pageSize}`;
			const { data } = await Axios({ ...getPaymentsList(query) });
			const responseData = data?.data || {};
			setPayments((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error("Error fetching payments:", error);
			setPayments([]);
		} finally {
			setLoading(false);
		}
	}, [paginationModel.page, paginationModel.pageSize]);

	const fetchConfig = useCallback(async () => {
		try {
			const { data } = await Axios({ ...getPaymentConfigsURL({ page: 0, size: 20 }) });
			const list = data?.data?.data || [];
			const payoutCfg = list.find(
				(c: any) => c.type === "MERCHANT_PAYOUT_CRON_JOB"
			);
			if (payoutCfg) {
				setCronConfig({
					id: payoutCfg.id,
					cron: payoutCfg.properties?.cronExpression || "",
				});
			}
		} catch (error) {
			console.error("Error fetching payout config:", error);
		}
	}, []);

	useEffect(() => {
		fetchPayments();
	}, [fetchPayments]);

	useEffect(() => {
		fetchConfig();
	}, [fetchConfig]);

	const handleRunPayouts = async () => {
		try {
			setRunning(true);
			const { data } = await Axios({ ...processPayoutsURL });
			message.success(data?.message || "Payout settlement run completed");
			await fetchPayments();
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || "Failed to run payout settlement"
			);
		} finally {
			setRunning(false);
		}
	};

	const handleMarkEligible = async (id: string) => {
		try {
			const { data } = await Axios({ ...markPayoutEligibleURL(id) });
			message.success(data?.message || "Payout marked eligible");
			await fetchPayments();
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || "Failed to mark payout eligible"
			);
		}
	};

	const handleSaveCron = async () => {
		if (!cronConfig.id) {
			message.error("No payout cron config found to update.");
			return;
		}
		try {
			setSavingCron(true);
			const { data } = await Axios({
				...updatePaymentConfigURL(cronConfig.id),
				data: { properties: { cronExpression: cronConfig.cron } },
			});
			message.success(data?.message || "Payout schedule updated");
		} catch (error: any) {
			message.error(
				error?.response?.data?.message || "Failed to update payout schedule"
			);
		} finally {
			setSavingCron(false);
		}
	};

	// Columns are built here so the action cell can call the handlers.
	const columns: any[] = [
		{ headerName: "Merchant", field: "merchantId" },
		{ headerName: "Order", field: "order" },
		{ headerName: "Gross", field: "gross" },
		{ headerName: "Commission", field: "commission" },
		{ headerName: "Net Payable", field: "net" },
		{
			headerName: "Eligible",
			field: "eligibleBool",
			renderCell: ({ row }: any) => (
				<Chip
					label={row.eligibleBool ? "Yes" : "No"}
					color={row.eligibleBool ? "info" : "default"}
					size="small"
					variant="outlined"
				/>
			),
		},
		{
			headerName: "Status",
			field: "status",
			renderCell: ({ row }: any) => (
				<Chip label={row.status || "-"} color={statusColor(row.status)} size="small" />
			),
		},
		{ headerName: "Date", field: "date" },
		{
			headerName: "Action",
			field: "action",
			sortable: false,
			renderCell: ({ row }: any) =>
				row.status === "PENDING" && !row.eligibleBool ? (
					<Button
						size="small"
						variant="outlined"
						onClick={() => handleMarkEligible(row.id)}
					>
						Mark Eligible
					</Button>
				) : (
					<></>
				),
		},
	];

	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return payments;
		return payments.filter(
			(r) =>
				r.merchantId.toLowerCase().includes(q) ||
				r.order.toLowerCase().includes(q)
		);
	}, [payments, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	return (
		<Card className="my-3 p-3 md:p-5">
			<Box className="flex flex-col md:flex-row md:items-center justify-between gap-3">
				<FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium}>
					Payments
				</FaiTypography>
				<Button
					variant="contained"
					color="primary"
					disabled={running}
					onClick={handleRunPayouts}
				>
					{running ? "Running…" : "Run Payouts Now"}
				</Button>
			</Box>
			<p className="text-sm mb-4 mt-1">
				Merchant payouts across the platform (net of commission). “Run Payouts
				Now” settles every <b>eligible</b> payout immediately — the same logic
				the cron runs on a schedule. Use “Mark Eligible” to stage a pending
				payout for the next run.
			</p>

			{/* Payout schedule (cron) editor */}
			<Box
				sx={{ bgcolor: "#F5F5F5", borderRadius: 2, p: 2 }}
				className="flex flex-col md:flex-row md:items-end gap-3 mb-4"
			>
				<Box className="flex-1">
					<FaiTypography
						variant="caption"
						className="!font-semibold !text-sm"
					>
						Payout schedule (cron expression)
					</FaiTypography>
					<TextField
						fullWidth
						size="small"
						placeholder="e.g. 0 0 2 * * ? *"
						value={cronConfig.cron}
						onChange={(e) =>
							setCronConfig((prev) => ({ ...prev, cron: e.target.value }))
						}
						helperText="Quartz cron — when the automatic payout job runs."
					/>
				</Box>
				<Button
					variant="outlined"
					disabled={savingCron || !cronConfig.id}
					onClick={handleSaveCron}
				>
					{savingCron ? "Saving…" : "Save Schedule"}
				</Button>
			</Box>

			<Box className="flex flex-col md:flex-row justify-between items-center my-4 w-full gap-3">
				<SearchInput
					placeholder="Search By Merchant / Order"
					value={search}
					onChange={(e: any) => setSearch(e.target.value)}
				/>
			</Box>
			<Box sx={{ mt: 2 }}>
				{loading ? (
					<Box className="flex justify-center p-6">
						<CircularProgress />
					</Box>
				) : (
					<FaiDataGrid
						rows={visibleRows}
						columns={columns}
						pagination={true}
						pageSize={paginationModel.pageSize}
						rowCount={rowCount}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
					/>
				)}
			</Box>
		</Card>
	);
}

export default Payments;
